/**
 * Median (GoNative) JavaScript Bridge wrapper
 * https://median.co/docs/javascript-bridge
 * 
 * Provides type-safe access to native app functionality
 * when running inside Median WebView wrapper.
 */

declare global {
  interface Window {
    median?: {
      navigation: {
        /** Prevent default back behavior and call callback instead */
        setBackHandler: (handler: (() => void) | null) => void;
        /** Navigate back in WebView history */
        goBack: () => void;
        /** Check if can go back */
        canGoBack: () => Promise<boolean>;
      };
      app: {
        /** Close the app */
        exit: () => void;
      };
    };
    gonative?: Window['median']; // Legacy GoNative alias
  }
}

/**
 * Check if Median bridge is available
 */
export function hasMedianBridge(): boolean {
  return !!(window.median || window.gonative);
}

/**
 * Get Median bridge (median or gonative)
 */
export function getMedianBridge() {
  return window.median || window.gonative;
}

/**
 * Set custom back handler for Median
 * Pass null to restore default behavior
 */
export function setMedianBackHandler(handler: (() => void) | null): void {
  const bridge = getMedianBridge();
  if (bridge?.navigation?.setBackHandler) {
    bridge.navigation.setBackHandler(handler);
  }
}

/**
 * Exit the app via Median
 */
export function exitApp(): void {
  const bridge = getMedianBridge();
  if (bridge?.app?.exit) {
    bridge.app.exit();
  } else {
    // Fallback: try to close window (won't work in WebView but safe to call)
    window.close();
  }
}

/**
 * Navigate back via Median
 */
export function medianGoBack(): void {
  const bridge = getMedianBridge();
  if (bridge?.navigation?.goBack) {
    bridge.navigation.goBack();
  } else {
    window.history.back();
  }
}
